"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";
import { Check, ChevronsUpDown } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";

interface PhoneNumberInputProps {
  id: string;
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
}

function countryFlag(country: CountryCode): string {
  return String.fromCodePoint(
    ...country
      .toUpperCase()
      .split("")
      .map((letter) => 127397 + letter.charCodeAt(0)),
  );
}

export default function PhoneNumberInput({
  id,
  country,
  onCountryChange,
  value,
  onChange,
  invalid = false,
  describedBy,
}: PhoneNumberInputProps) {
  const t = useTranslations("publicEventSignup");
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const countries = useMemo(() => {
    const displayNames = new Intl.DisplayNames([locale], { type: "region" });

    return getCountries()
      .map((countryCode) => ({
        code: countryCode,
        name: displayNames.of(countryCode) ?? countryCode,
        callingCode: getCountryCallingCode(countryCode),
      }))
      .sort((left, right) => left.name.localeCompare(right.name, locale));
  }, [locale]);

  const selectedCallingCode = getCountryCallingCode(country);

  return (
    <div
      className={cn(
        "border-input flex h-10 w-full rounded-full border bg-transparent shadow-xs transition-[color,box-shadow]",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        invalid &&
          "border-destructive ring-destructive/20 dark:ring-destructive/40",
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="h-full rounded-r-none border-r px-3 text-base"
            aria-label={t("countryCodeLabel")}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <span aria-hidden="true">{countryFlag(country)}</span>
            <span>+{selectedCallingCode}</span>
            <ChevronsUpDown className="size-4 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-2">
          <Command>
            <CommandInput placeholder={t("countryCodeSearchPlaceholder")} />
            <CommandList className="max-h-64">
              <CommandEmpty>{t("countryCodeNoResults")}</CommandEmpty>
              <CommandGroup>
                {countries.map((option) => (
                  <CommandItem
                    key={option.code}
                    value={`${option.name} ${option.code} +${option.callingCode}`}
                    onSelect={() => {
                      onCountryChange(option.code);
                      setOpen(false);
                    }}
                  >
                    <span aria-hidden="true">{countryFlag(option.code)}</span>
                    <span className="min-w-0 flex-1 truncate">
                      {option.name}
                    </span>
                    <span className="text-muted-foreground">
                      +{option.callingCode}
                    </span>
                    <Check
                      className={cn(
                        "size-4",
                        country === option.code ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className="h-full rounded-l-none border-0 shadow-none focus-visible:border-transparent focus-visible:ring-0"
        value={value}
        placeholder={t("phoneNationalPlaceholder")}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
